import React, { useState } from 'react';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button, Modal, FormInput, Toggle, FormMultiSelect } from '../../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Gamepad2, Flame, Heart, Zap, Trophy, Gift, Target, Sparkles, Crown, Swords, Dice5, Ghost, Music, Monitor, LayoutGrid,
  Spade, Club, Diamond, Medal, Activity, FastForward, Timer, Joystick, Cpu, Smartphone,
  Users, TrendingUp, Rocket, Globe, Shield, Wallet, Bell, MessageSquare, Camera, Image,
  Coffee, Pizza, Beer, Wine, Settings, Puzzle, Compass, Map, Plane, Car, Ship, Key, Layers,
  Dice6, Radar, Flag, Goal, Magnet
} from 'lucide-react';

const fetchProvidersAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/providers', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

interface Group {
  id: number;
  name: string;
  sort_order: number;
  admin_is_enabled: boolean;
  show_on_home_index?: boolean;
  show_on_home_casino?: boolean;
  show_as_tab_casino?: boolean;
  show_on_home_sports?: boolean;
  show_as_tab_sports?: boolean;
  icon?: string;
}

const COMMON_ICONS = [
  { id: "Gamepad2", icon: Gamepad2 },
  { id: "Flame", icon: Flame },
  { id: "Heart", icon: Heart },
  { id: "Zap", icon: Zap },
  { id: "Trophy", icon: Trophy },
  { id: "Gift", icon: Gift },
  { id: "Target", icon: Target },
  { id: "Sparkles", icon: Sparkles },
  { id: "Crown", icon: Crown },
  { id: "Swords", icon: Swords },
  { id: "Dice5", icon: Dice5 },
  { id: "Ghost", icon: Ghost },
  { id: "Music", icon: Music },
  { id: "Monitor", icon: Monitor },
  { id: "LayoutGrid", icon: LayoutGrid },
  { id: "Spade", icon: Spade },
  { id: "Club", icon: Club },
  { id: "Diamond", icon: Diamond },
  { id: "Medal", icon: Medal },
  { id: "Activity", icon: Activity },
  { id: "FastForward", icon: FastForward },
  { id: "Timer", icon: Timer },
  { id: "Joystick", icon: Joystick },
  { id: "Cpu", icon: Cpu },
  { id: "Smartphone", icon: Smartphone },
  { id: "Users", icon: Users },
  { id: "TrendingUp", icon: TrendingUp },
  { id: "Rocket", icon: Rocket },
  { id: "Globe", icon: Globe },
  { id: "Shield", icon: Shield },
  { id: "Wallet", icon: Wallet },
  { id: "Bell", icon: Bell },
  { id: "MessageSquare", icon: MessageSquare },
  { id: "Camera", icon: Camera },
  { id: "Image", icon: Image },
  { id: "Coffee", icon: Coffee },
  { id: "Pizza", icon: Pizza },
  { id: "Beer", icon: Beer },
  { id: "Wine", icon: Wine },
  { id: "Settings", icon: Settings },
  { id: "Puzzle", icon: Puzzle },
  { id: "Compass", icon: Compass },
  { id: "Map", icon: Map },
  { id: "Plane", icon: Plane },
  { id: "Car", icon: Car },
  { id: "Ship", icon: Ship },
  { id: "Key", icon: Key },
  { id: "Layers", icon: Layers },
  { id: "Dice6", icon: Dice6 },
  { id: "Radar", icon: Radar },
  { id: "Flag", icon: Flag },
  { id: "Goal", icon: Goal },
  { id: "Magnet", icon: Magnet }
];


const fetchGroupsAPI = async (): Promise<Group[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/groups`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const Groups: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: groups = [], isLoading } = useQuery({ queryKey: ['groups'], queryFn: fetchGroupsAPI });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Group>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [manageGamesGroupId, setManageGamesGroupId] = useState<number | null>(null);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  
  const { data: providers = [] } = useQuery({ queryKey: ['providers'], queryFn: fetchProvidersAPI });
  
  const assignMutation = useMutation({
    mutationFn: async (provider_ids: string[]) => {
      const token = localStorage.getItem('token');
      return apiClient.post(`/api/game-engine/admin/groups/${manageGamesGroupId}/assign-games`, { provider_ids }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      toast.success(res.data.message || 'Games assigned to group!');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setManageGamesGroupId(null);
      setSelectedProviderIds([]);
    },
    onError: () => toast.error('Failed to assign games')
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Group>) => {
      const token = localStorage.getItem("token");
      if (data.id) return apiClient.put(`/api/game-engine/admin/groups/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      return apiClient.post(`/api/game-engine/admin/groups`, data, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success(variables.id ? "Group updated!" : "Group created!");
      closeModal();
    },
    onError: () => toast.error("Failed to save group")
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem("token");
      return apiClient.delete(`/api/game-engine/admin/groups/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success("Group deleted!");
    },
    onError: () => toast.error("Failed to delete group")
  });

  const openModal = (grp?: Group) => {
    if (grp) { setFormData(grp); setIsEdit(true); }
    else { 
      setFormData({ 
        name: '', sort_order: 0, admin_is_enabled: true,
        show_on_home_index: false, show_on_home_casino: false, show_as_tab_casino: false,
        show_on_home_sports: false, show_as_tab_sports: false,
        icon: 'Gamepad2'
      }); 
      setIsEdit(false); 
    }
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setFormData({}); };
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); saveMutation.mutate(formData); };

  const filtered = groups.filter((a: any) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const paginated = filtered.slice((page - 1) * limit, page * limit);

  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { 
      key: 'name', 
      label: 'Name',
      render: (val: string, item: Group) => {
        const IconData = COMMON_ICONS.find(i => i.id === item.icon);
        const IconComponent = IconData ? IconData.icon : null;
        return (
          <div className="flex items-center gap-2">
            {IconComponent && <IconComponent size={16} className="text-indigo-400 shrink-0" />}
            <span className="font-semibold text-slate-200">{val}</span>
          </div>
        );
      }
    },
    { key: 'game_count', label: 'Games Count', render: (val: number) => <span className="font-bold text-slate-300">{val || 0}</span> },
    { key: 'sort_order', label: 'Sort Order' },
    {
      key: 'admin_is_enabled', label: 'Admin Status',
      render: (val: boolean, item: Group) => <Toggle checked={val} onChange={(c) => saveMutation.mutate({ ...item, admin_is_enabled: c })} />
    },
    {
      key: 'actions', label: 'Actions', textAlign: 'right' as const,
      render: (_: any, item: Group) => (
        <div className="flex items-center justify-end gap-2">
                    <Button variant="primary" size="xs" onClick={() => setManageGamesGroupId(item.id)}>Manage Games</Button>
          <Button variant="ghost" size="xs" onClick={() => openModal(item)}>Edit</Button>
          <Button variant="danger" size="xs" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate(item.id)}>Delete</Button>
        </div>
      )
    }
  ];

  return (
    <>
      <StakeTable
        title="Game Groups"
        columns={columns}
        data={paginated}
        isLoading={isLoading}
        searchPlaceholder="Search groups..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={filtered.length}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No groups found."
        actions={<Button variant="success" onClick={() => openModal()}>+ Add Group</Button>}
      />

            <Modal open={isModalOpen} onClose={closeModal} title={isEdit ? 'Edit Group' : 'Add Group'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveMutation.isPending} onClick={handleSubmit}>{isEdit ? 'Save Changes' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <FormInput type="number" label="Sort Order" required value={formData.sort_order || 0} onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} />
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Group Icon Selection</label>
            <div className="grid grid-cols-10 gap-2 p-3 bg-slate-950/50 border border-slate-700/50 rounded-lg max-h-40 overflow-y-auto">
              {COMMON_ICONS.map((item) => {
                const IconComponent = item.icon;
                const isSelected = formData.icon === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: item.id })}
                    title={item.id}
                    className={`aspect-square p-2 rounded-lg flex items-center justify-center transition-all ${
                      isSelected 
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 scale-110' 
                        : 'bg-slate-900 border border-slate-800 text-slate-400 hover:border-slate-600 hover:text-white'
                    }`}
                  >
                    <IconComponent size={16} />
                  </button>
                );
              })}
            </div>
            {formData.icon && (
              <div className="flex items-center gap-2 mt-1 text-xs text-indigo-400 font-bold uppercase tracking-wider">
                Selected Icon: {formData.icon}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show on Home Index</span>
                <Toggle checked={!!formData.show_on_home_index} onChange={c => setFormData({...formData, show_on_home_index: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show on Casino Home</span>
                <Toggle checked={!!formData.show_on_home_casino} onChange={c => setFormData({...formData, show_on_home_casino: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show as Casino Tab</span>
                <Toggle checked={!!formData.show_as_tab_casino} onChange={c => setFormData({...formData, show_as_tab_casino: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show on Sports Home</span>
                <Toggle checked={!!formData.show_on_home_sports} onChange={c => setFormData({...formData, show_on_home_sports: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show as Sports Tab</span>
                <Toggle checked={!!formData.show_as_tab_sports} onChange={c => setFormData({...formData, show_as_tab_sports: c})} />
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={manageGamesGroupId !== null} onClose={() => setManageGamesGroupId(null)} title="Assign Games to Group"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setManageGamesGroupId(null)}>Cancel</Button>
            <Button 
              variant="primary" 
              size="sm" 
              disabled={selectedProviderIds.length === 0} 
              loading={assignMutation.isPending} 
              onClick={() => assignMutation.mutate(selectedProviderIds)}
            >
              Assign Provider Games
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Select one or more Providers to bulk-assign all their active games into this group. You can also assign games manually via the Games Management page.</p>
          <div>
            <FormMultiSelect
              label="Providers"
              placeholder="Search and Select Providers"
              options={providers.filter((p: any) => p.developer_is_enabled !== false).map((prov: any) => ({
                value: String(prov.id),
                label: prov.name
              }))}
              selectedValues={selectedProviderIds}
              onChange={setSelectedProviderIds}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
export default Groups;
